import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { CoreService } from 'src/app/api/core/core/core.service';
import { DocketService } from 'src/app/api/core/docket/docket.service';
import { AlertQueueService } from 'src/app/api/utils/prompt-queue/alert-queue.service';
import { IItemSummary } from 'src/app/models/items/items.interface';
import { CONTEXT_TYPE, Order } from 'src/app/models/order.model';

import * as moment from 'moment-timezone';
import { UtilityClass } from 'src/app/api/utils/UtilityClass';

@Component({
  selector: 'hub-eta-selector',
  templateUrl: './eta-selector.component.html',
  styleUrls: ['./eta-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class EtaSelectorComponent implements OnInit {
  /** The order to set the ETA */
  @Input() order: Order;

  /** The number of orders that will have eta selector */
  @Input() pending: number;

  /** The summarized list of order items */
  public orderSummary: IItemSummary[];

  /** The clone of order sent so we don't change the ETA in real time */
  public activeOrder: Order;

  /** The display state of the modal */
  public etaTimeState: 'default' | 'change' | 'custom' = 'default';

  /** The human readable representation of ETA to display */
  public tempEtaStr: string;
  public tempDateStr: string;
  public tempDayStr: string;

  // the moment Obj of ETA
  public tempEta: moment.Moment;

  // a copy of server time
  public baseServerTime: moment.Moment;

  // Flagger used to check if order is ASAP
  public isASAP: boolean = false;

  public eta_message: string;

  constructor(
    public modalCtrl: ModalController,
    public docketService: DocketService,
    public core: CoreService,
    public changeRef: ChangeDetectorRef,
    public alertCtrl: AlertController,
    public alertQueue: AlertQueueService,
  ) {}

  ngOnInit() {
    this.populateData();
    if (this.activeOrder) {
      this.presetEta();
    }
  }

  /**
   * Initialises the modal and updates all the display information
   */
  populateData(): void {
    if (!this.order) {
      this.close();
      return;
    } else {
      // just to make sure we have the right object
      this.activeOrder = this.docketService.getOrderById(this.order.id);
    }

    if (this.activeOrder) {
      this.orderSummary = this.activeOrder.getOrderSummary();

      // get difference between requested and created.
      let requested_time: string = 'ASAP';
      if (this.activeOrder.requested_time) {
        if (this.activeOrder.created_time) {
          const req_time = this.getLocalizedTime(this.activeOrder.requested_time);
          const create_time = this.getLocalizedTime(this.activeOrder.created_time);
          const diff = req_time.diff(create_time, 'ms');

          const useEtaOrRequestedTime = this.activeOrder.eta ? this.activeOrder.eta : this.activeOrder.requested_time;
          const isPreOrder: boolean = this.getLocalizedTime(useEtaOrRequestedTime).isAfter(create_time, 'day');

          // check if it is considered 'ASAP' = less than req_minutes_diff
          if (diff < this.core.asap_required_time * 60000) {
            // this is ASAP
          } else {
            requested_time = this.getLocalizedTime(this.activeOrder.requested_time).format(
              isPreOrder ? '[for] dddd, MMMM D [at] h:mma' : '[at] h:mma',
            );
          }
        } else {
          requested_time = this.getLocalizedTime(this.activeOrder.requested_time).format(
            '[for] dddd, MMMM D [at] h:mma',
          );
        }
      }

      const name: string = `${this.activeOrder.customer.first_name} ${this.activeOrder.customer.last_name}`;
      if (this.order.is_delivery_window) {
        let requested_time_end: string = this.getLocalizedTime(this.activeOrder.requested_time_end).format('h:mma');
        requested_time = `${requested_time} - ${requested_time_end}`;
      }

      switch (this.activeOrder.context_type) {
        case CONTEXT_TYPE.Delivery:
          this.eta_message = `${name} has requested  <span class="semibold delivery-text-color"> &nbsp; Delivery &nbsp;</span> to ${this.order.customer.address.suburb} ${requested_time}`;
          break;
        case CONTEXT_TYPE.Pickup:
          this.eta_message = `${name} has requested  <span class="semibold onlinepickup-text-color">&nbsp; Pickup &nbsp;</span> ${requested_time}`;
          break;
        case CONTEXT_TYPE.Dine_in:
          if (this.activeOrder.is_collect_at_counter) {
            this.eta_message =
              'Table ' + this.activeOrder.getTableLabel() + ` for ${this.activeOrder.customer.first_name}`;
          } else {
            this.eta_message = `Order for #${this.activeOrder.id}`;
          }
          break;
        case CONTEXT_TYPE.Room_service:
          this.eta_message = `${name} has requested  <span class="semibold room_service-text-color">&nbsp; Room Service &nbsp;</span> ${requested_time}`;
          break;
        case CONTEXT_TYPE.Drive_up:
          this.eta_message = `${name} has requested  <span class="semibold drive_up-text-color">&nbsp; Drive up &nbsp;</span> ${requested_time}`;
          break;
      }
    } else {
      // just close the modal
      this.modalCtrl.dismiss();
    }

    this.changeRef.detectChanges();
  }

  getLocalizedTime(time: string) {
    return UtilityClass.parseTimeToTimezone(time, this.core.Timezone);
  }

  /**
   * Calculates the initial ETA to be displayed and suggested to the user
   */
  presetEta(): void {
    // make a copy so we don't change the actual time
    this.baseServerTime = this.core.Server_time;

    // round off
    const remainder: number = 5 - (this.baseServerTime.minute() % 5);
    this.baseServerTime.add(remainder, 'minutes');

    // set ETA to either requested_time, created_time, or now.
    if (this.activeOrder.requested_time && this.activeOrder.created_time) {
      // get difference between requested and created.
      const req_time = this.getLocalizedTime(this.activeOrder.requested_time);
      const create_time = this.getLocalizedTime(this.activeOrder.created_time);
      const diff = req_time.diff(create_time, 'ms');

      // check if it is considered 'ASAP' = less than req_minutes_diff
      if (diff < this.core.asap_required_time * 60000) {
        // check if ASAP and has suggested time available
        // DO IT HERE BECAUSE WE NEED THE ASAP FLAGGER BELOW TO UPDATE
        if (this.activeOrder.suggested_time) {
          // round up to the closest 5
          let suggestedTime = this.getLocalizedTime(this.activeOrder.suggested_time);

          const remainder: number = 5 - (suggestedTime.minute() % 5);
          if (remainder != 5) {
            suggestedTime.add(remainder, 'minutes');
          }

          this.tempEta = moment(suggestedTime);
        } else {
          this.tempEta = moment(this.baseServerTime);
        }

        this.updateTimeString();

        // req_time is considered ASAP
        this.isASAP = true;
      } else {
        // round up to the closest 5
        const remainder: number = 5 - (req_time.minute() % 5);
        if (remainder != 5) {
          req_time.add(remainder, 'minutes');
        }

        this.tempEta = moment(req_time);
        this.updateTimeString();

        // NOT considered ASAP
        this.isASAP = false;
      }
    } else {
      this.tempEta = moment(this.baseServerTime);
      this.updateTimeString();
    }

    // make sure that initial time is greater than server time
    if (this.tempEta.diff(this.baseServerTime, 'minutes') <= 0) {
      this.tempEta = moment(this.baseServerTime);
      this.updateTimeString();
    }

    this.changeRef.detectChanges();
  }

  /**
   * Updates all the human readable time in display
   */
  updateTimeString(): void {
    this.tempEtaStr = this.tempEta.format('h:mmA');
    this.tempDateStr = this.tempEta.format('MMMM Do');
    this.tempDayStr = this.tempEta.format('dddd');
  }

  /**
   * Increase the value of the dummy ETA
   * @param val - The number value to add
   * @param unit The unit value to use. Acceptable values are minute and day.
   */
  addTime(val: number, unit: 'minute' | 'day') {
    this.tempEta.add(val, unit);
    this.updateTimeString();
    this.changeRef.detectChanges();
  }

  /**
   * Change the display state of the modal
   * @param state The display state to transition. Accepted values are default, change and custom.
   */
  changeTimeState(state: 'default' | 'change' | 'custom'): void {
    this.etaTimeState = state;
    this.changeRef.detectChanges();
  }

  /** Save the dummy ETA as the actual ETA of the order. */
  async confirm() {
    let alert;

    // show error message
    if (this.tempEta.isBefore(this.baseServerTime)) {
      alert = await this.alertCtrl.create({
        subHeader: this.activeOrder.context_title + ' ETA',
        message:
          'Sorry! The ETA must be some time in the future. Current store time is ' +
          this.baseServerTime.format('dddd, D MMMM, h:mm a') +
          '.',
      });

      this.alertQueue.queueAlert(alert);
    } else {
      this.modalCtrl.dismiss({ eta: this.tempEta });
    }
  }

  close(dismissAll: boolean = false): void {
    this.modalCtrl.dismiss({
      dismiss_all: dismissAll,
    });
  }
}
